import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { faBoxOpen, faCalendar, faFileExcel, faLayerGroup, faSearch } from '@fortawesome/free-solid-svg-icons';
import { ColumnModel, FilterSettingsModel, GridComponent, PageSettingsModel } from '@syncfusion/ej2-angular-grids';
import { finalize } from 'rxjs/operators';
import { ErrorService } from '../../../../../@core/utils/error.service';
import { FilterSettings } from '../../../../core/widgets/kw-filter-dropdown/models/filter-options';
import { FilterResults } from '../../../../core/widgets/kw-filter-dropdown/models/filter-results';
import { InventoryService } from '../../../pick-plan/services/inventory.service';

const MOBILE_PAGE_SIZE = 25;

export interface LotMassBalance {
  balance: number;
  lotNumber: string;
  date: Date;
}

export interface ReportDto {
  packoutCode: string;
  totalQuantity: number;
  lots: string;
  date: Date;
  lotMassBalances: LotMassBalance[];
}

@Component({
  selector: 'packout-report',
  templateUrl: './packout-report.component.html',
  styleUrls: ['./packout-report.component.scss']
})
export class PackoutReportComponent implements OnInit {
  @ViewChild('grid') public grid: GridComponent;
  @ViewChild('detailTemplate', { static: true }) public detailTemplate: any;

  public faBoxOpen = faBoxOpen;
  public faCalendar = faCalendar;
  public faFileExcel = faFileExcel;
  public faLayerGroup = faLayerGroup;
  public faSearch = faSearch;

  public isLoading = false;
  public searchTerm = '';
  public rows: ReportDto[] = [];
  private allRows: ReportDto[] = [];
  public mobileVisibleCount = MOBILE_PAGE_SIZE;
  public expandedCodes = new Set<string>();

  public pageSettings: PageSettingsModel = { pageSize: 50, pageSizes: [50, 100, 200] };
  public filterSettings: FilterSettingsModel = { mode: 'Immediate', type: 'Excel' };
  public kwFilterSettings: FilterSettings = {
    cropYearSettings: { display: true, singleSelection: true }
  };
  private filterResults: FilterResults = {};

  public columns: ColumnModel[] = [
    { field: 'packoutCode', headerText: 'Packout Code', width: 180 },
    { field: 'totalQuantity', headerText: 'Total Quantity', width: 150, format: 'N2', textAlign: 'Right' },
    { field: 'lots', headerText: 'Lots', width: 320, clipMode: 'EllipsisWithTooltip' },
    { field: 'date', headerText: 'Date', width: 150, type: 'date', format: 'yMd' }
  ];

  public lotColumns: ColumnModel[] = [
    { field: 'lotNumber', headerText: 'Lot Number', width: 180 },
    { field: 'balance', headerText: 'Quantity', width: 130, format: 'N2', textAlign: 'Right' },
    { field: 'date', headerText: 'Date', width: 150, type: 'date', format: 'yMd' }
  ];

  private destroyRef = inject(DestroyRef);

  constructor(
    private errorService: ErrorService,
    private inventoryService: InventoryService
  ) { }

  ngOnInit() {
    this.loadPackoutReport();
  }

  loadPackoutReport() {
    const selectedYear = this.filterResults.selectedCropYears?.[0];
    const cropYear = selectedYear?.id != null ? selectedYear.itemName : new Date().getFullYear().toString();

    this.isLoading = true;
    this.inventoryService.getPackoutReport(cropYear)
      .pipe(finalize(() => this.isLoading = false), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: ReportDto[]) => {
          this.allRows = response ?? [];
          this.applySearch();
        },
        error: err => this.errorService.handleError(err)
      });
  }

  search(newFilters: FilterResults) {
    const previousId = this.filterResults.selectedCropYears?.[0]?.id;
    const nextId = newFilters.selectedCropYears?.[0]?.id;
    const yearChanged = this.filterResults.selectedCropYears != null && newFilters.selectedCropYears != null && nextId !== previousId;

    this.filterResults = { ...newFilters };
    if (yearChanged)
      this.loadPackoutReport();
  }

  applySearch() {
    const term = this.searchTerm.trim().toLowerCase();
    this.rows = term
      ? this.allRows.filter(row => [row.packoutCode, row.lots].some(value => value?.toLowerCase().includes(term)))
      : [...this.allRows];
    this.mobileVisibleCount = MOBILE_PAGE_SIZE;
  }

  exportExcel() {
    this.grid?.excelExport({ fileName: 'PackoutReport.xlsx' });
  }

  toggleLots(row: ReportDto) {
    if (!this.expandedCodes.delete(row.packoutCode))
      this.expandedCodes.add(row.packoutCode);
  }

  showMoreMobile() {
    this.mobileVisibleCount += MOBILE_PAGE_SIZE;
  }

  trackByRow(index: number, row: ReportDto): string {
    return `${row.packoutCode}-${index}`;
  }
}
